import * as React from 'react'
import { Badge, Button, Card, Collection, Divider, Flex, Heading, Loader, Message, Text, View } from '@aws-amplify/ui-react'
import { PeanutsPack } from '@mytmpvpn/mytmpvpn-common/models/peanuts'
import { useComponentSize } from './ResponsiveSize'
import { MyTmpVpnClient } from '@mytmpvpn/mytmpvpn-client'
import { useErrorContext } from '../ErrorContext'
import { useNavigate } from 'react-router-dom'
import { AuthUser, fetchUserAttributes } from '@aws-amplify/auth'
import { useAuthenticator } from '../amplify'
import { retrieveStoredReferralCode } from '../utils/referralUtils'
import { logger } from '../logging'


async function maybeRedirectToUrl(url: any, user: AuthUser, setError: any) {
  try {
    const userAttributes = await fetchUserAttributes()

    const result = Object.entries(userAttributes).map(([key, value]) => ({
      getName: () => key,
      getValue: () => value
    }))

    if (result === undefined) {
      setError(`Can't get user attributes. Please logout and retry. If that does not work, create an issue`)
      return
    }

    var sub = undefined
    for (var i = 0; i < result.length; i++) {
      if (result[i].getName() === 'sub') {
        sub = result[i].getValue()
        break
      }
    }

    if (sub === undefined) {
      setError(`Can't get user sub attributes. Please logout and retry. If that does not work, create an issue`)
      return
    }

    logger.log("sub", sub)
    let cri = sub

    const refCode = retrieveStoredReferralCode()
    if (refCode) {
      logger.log("Refcode found in storage: ", refCode)
      if (refCode.length !== 8) {
        setError(`Referral code must be 8 characters long`)
        return
      }
      cri += refCode
    }
    var urlSuffix = `?client_reference_id=${encodeURIComponent(cri)}`
    urlSuffix += userAttributes.email ? `&prefilled_email=${encodeURIComponent(userAttributes.email)}` : ''

    const coupon = new URLSearchParams(window.location.search).get('coupon')
    if (coupon) {
      urlSuffix += `&prefilled_promo_code=${coupon}`
    }

    var fullUrl = new URL(urlSuffix, url).href
    logger.log("fullUrl: ", urlSuffix)

    window.open(fullUrl, '_blank')
  } catch (err) {
    setError(err)
  }
}

function PlanButton({ url }: { url: URL }) {
  const navigate = useNavigate()
  const { setError } = useErrorContext()
  const { authStatus, user } = useAuthenticator((context) => [context.authStatus, context.user])

  const signed = authStatus === 'authenticated'
  const label = signed ? "Buy" : "Sign In"

  const action = signed ?
    () => maybeRedirectToUrl(url, user, setError) :
    () => navigate('/login')

  return (
    <Button onClick={action}>
      {label}
    </Button>
  )
}


interface PeanutsPacksGridProps {
  client: MyTmpVpnClient
}

/**
 * Shared pricing cards grid used by both the Welcome page and the Pricing page.
 * Includes a Buy/Sign In button on each card.
 */
export function PeanutsPacksGrid({ client }: PeanutsPacksGridProps) {
  const [peanutsPacks, setPeanutsPacks] = React.useState<PeanutsPack[]>([])
  const { setError } = useErrorContext()
  const componentSize = useComponentSize()

  React.useEffect(() => {
    const fetchPacks = async () => {
      try {
        const packs = await client.listPeanutsPacks()
        setPeanutsPacks(packs)
      } catch (e: any) {
        setError(e)
      }
    }
    fetchPacks()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (peanutsPacks.length === 0) {
    return <Loader variation="linear" />
  }

  return (
    <Flex direction="column" alignItems="center" gap="0.75rem">
      <Collection
        items={peanutsPacks}
        type="list"
        direction={componentSize === 'short' ? 'column' : 'row'}
        alignSelf="center"
        gap={componentSize === 'short' ? '12px' : '20px'}
        wrap="wrap"
      >
        {(pack) => (
          <Card
            key={pack.name}
            borderRadius="medium"
            variation="outlined"
            padding={componentSize === 'short' ? '0.75rem' : '1rem'}
          >
            <Flex direction="column" alignItems="center" gap="0.5rem">
              <Heading level={componentSize === 'short' ? 5 : 4}>{pack.name}</Heading>
              <Divider />
              <Text fontWeight="bold" fontSize="large">{pack.peanuts} 🥜</Text>
              <Badge size="large">${pack.price} USD</Badge>
              <View textAlign="left" alignSelf="stretch" fontSize="small">
                <ul style={{ paddingLeft: '1.2rem', margin: '0.25rem 0' }}>
                  {pack.description.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </View>
              <PlanButton url={pack.url} />
            </Flex>
          </Card>
        )}
      </Collection>
      <Message variation="filled" colorTheme="info">
        <Text fontSize="small">Duration estimates vary across locations.</Text>
      </Message>
      <Message variation="filled" colorTheme="success">
        <Text fontSize="small">🔄 Unused peanuts are refundable — you only pay for what you use.</Text>
      </Message>
    </Flex>
  )
}
